import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { applyThousandSeperator } from '../../util';

import {
    getAmountBorrowed,
    getOtherAmountBorrowed,
    getInitiationFee,
    getServiceFee,
    getMinInstallment,
    getMaxInstallment,
    getMinCreditLife,
    getMaxCreditLife,
    getInterestRateText,
    getRepaymentTerm,
} from '@om/redux/selectors/personalLoansCalculator/calculator';
import {
    showChatBot,
} from '@om/redux/actions/publicWeb/chatBot';
import {
    getLoanAmountOptions,
} from '@om/redux/selectors/personalLoansCalculator/referenceData';
import { Om2ColLayout, OmLabeledValue, OmModal } from '@om/component-library-react';

const LoanInformationModal = () => {

    const dispatch = useDispatch();

    const {
        loanAmountOptions,
        amountBorrowed,
        otherAmountBorrowed,
        initiationFee,
        serviceFee,
        minTotalInstallment,
        maxTotalInstallment,
        minCreditLife,
        maxCreditLife,
        interestRate,
        repaymentTerm,
    } = useSelector(
        createStructuredSelector({
            loanAmountOptions: getLoanAmountOptions,

            amountBorrowed: getAmountBorrowed,
            otherAmountBorrowed: getOtherAmountBorrowed,

            initiationFee: getInitiationFee,
            serviceFee: getServiceFee,

            minTotalInstallment: getMinInstallment,
            maxTotalInstallment: getMaxInstallment,

            minCreditLife: getMinCreditLife,
            maxCreditLife: getMaxCreditLife,

            interestRate: getInterestRateText,

            repaymentTerm: getRepaymentTerm
        })
    );

    const getReferenceDataValue = (referenceDataArray, value) => {
        if (referenceDataArray && value) {
            return referenceDataArray.find((data) => data.code === value).value;
        }
        return ' ';
    }

    const capitalAmount = (() => {
        let capAmount = 0.00;

        if (otherAmountBorrowed.value !== null) {
            capAmount = applyThousandSeperator(otherAmountBorrowed.value);
        } else {
            capAmount = applyThousandSeperator(getReferenceDataValue(loanAmountOptions, amountBorrowed.value));
        }

        return `R${applyThousandSeperator(capAmount)}`;
    })();

    const serviceFeeAmount = (() => {
        let servFee = 0.00;

        if (parseFloat(serviceFee) > 0) {
            servFee = parseFloat(serviceFee).toFixed(2);
        }

        return `R${applyThousandSeperator(servFee)}`;
    })();

    const initiationFeeAmount = (() => {
        let initFee = 0.00;

        if (parseFloat(initiationFee) > 0) {
            initFee = parseFloat(initiationFee).toFixed(2);
        }

        return `R${applyThousandSeperator(initFee)}`;
    })();

    const installmentAmount = (() => {
        let minInstallment = 0.00;
        let maxInstallment = 0.00;

        if (parseFloat(minTotalInstallment) > 0) {
            minInstallment = parseFloat(minTotalInstallment).toFixed(2);
        }

        if (parseFloat(maxTotalInstallment) > 0) {
            maxInstallment = parseFloat(maxTotalInstallment).toFixed(2);
        }

        return `R${applyThousandSeperator(minInstallment)} - R${applyThousandSeperator(maxInstallment)}`;
    })();

    const creditLifeAmount = (() => {
        let minCredLife = 0.00;
        let maxCredLife = 0.00;

        if (parseFloat(minCreditLife) > 0) {
            minCredLife = parseFloat(minCreditLife).toFixed(2);
        }

        if (parseFloat(maxCreditLife) > 0) {
            maxCredLife = parseFloat(maxCreditLife).toFixed(2);
        }

        return `R${applyThousandSeperator(minCredLife)} - R${applyThousandSeperator(maxCredLife)}`;
    })();

    return (
        <div className="loan-information-modal">
            <OmModal
                isCentered={true}
                overlayName="more-info-modal"
                onOnClose={() => dispatch(showChatBot())}
            >
                <div slot="modal-content">
                    <h5><span className="medium">Details of</span> your loan </h5>
                    <div className="loan-information">
                        <div>
                            <Om2ColLayout>
                                <div slot="first">
                                    <OmLabeledValue label="Term:" value={`${repaymentTerm} months`} />
                                </div>
                                <div slot="second">
                                    <OmLabeledValue label="Capital:" value={capitalAmount} />
                                </div>
                            </Om2ColLayout>
                            <Om2ColLayout>
                                <div slot="first">
                                    <OmLabeledValue
                                        label="Initiation Fee:"
                                        value={initiationFeeAmount}
                                    />
                                </div>
                                <div slot="second">
                                    <OmLabeledValue
                                        label="Service Fee:"
                                        value={serviceFeeAmount}
                                    />
                                </div>
                            </Om2ColLayout>
                            <OmLabeledValue
                                label="Instalment:"
                                value={installmentAmount}
                            />
                            <OmLabeledValue
                                label="Interest Rate:"
                                value={interestRate}
                            />
                            <OmLabeledValue
                                label="Credit Life Insurance"
                                value={creditLifeAmount}
                            />
                        </div>
                        <hr />
                        <p><span className="medium">Note:</span> The results should be used as an indication only </p>
                    </div>
                </div>
            </OmModal>
        </div>
    )
}

export default LoanInformationModal;
